import React, { useState, useEffect, useCallback } from 'react';
import { Car, DollarSign, Users, Navigation, Truck, Package, Store, ShoppingBag, CheckCircle, ArrowRight } from 'lucide-react';
import apiService from '../apiService';
import { useAdmin } from '../context/AdminContext';

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('fr-FR'));

// Périodes du filtre « Vue d'ensemble ».
const PERIODS = [
  { key: 'day', label: 'Jour' },
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
  { key: 'year', label: 'Année' },
  { key: 'all', label: 'Depuis toujours' },
];

// Calcule la plage de dates (ISO) pour un preset — vide = depuis toujours.
function rangeFor(period) {
  const now = new Date();
  if (period === 'all') return { startDate: '', endDate: '' };
  const s = new Date(now);
  if (period === 'day') s.setHours(0, 0, 0, 0);
  else if (period === 'week') s.setDate(s.getDate() - 7);
  else if (period === 'month') s.setDate(s.getDate() - 30);
  else if (period === 'year') s.setFullYear(s.getFullYear() - 1);
  return { startDate: s.toISOString(), endDate: now.toISOString() };
}

// Carte statistique réutilisable — cliquable (navigue vers sa page) si onClick fourni.
function StatCard({ icon, label, value, sub, tint, onClick }) {
  return (
    <div
      className={`stat-card${onClick ? ' clickable' : ''}`}
      onClick={onClick}
      style={{ borderLeftColor: tint, ...(onClick ? { cursor: 'pointer' } : {}) }}
    >
      <div className="stat-icon" style={{ background: tint }}>{icon}</div>
      <div className="stat-info">
        <p className="stat-label">{label}</p>
        <h2 className="stat-value">{value}</h2>
        {sub != null && <p className="stat-change">{sub}</p>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { loadingDrivers, loadingSubscriptions, stats, setCurrentPage } = useAdmin();
  const [activeRidesCount, setActiveRidesCount] = useState(null);
  const [onlineDriversCount, setOnlineDriversCount] = useState(null);
  const [openBusinessesCount, setOpenBusinessesCount] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [period, setPeriod] = useState('month'); // filtre « Vue d'ensemble »
  const [overview, setOverview] = useState(null); // résumé abonnements sur la période

  const fetchRealtimeKPIs = useCallback(async () => {
    try {
      const [ridesRes, onlineRes, openRes] = await Promise.all([
        apiService.get('/admin/rides/active/count', { userType: 'admin' }).catch(() => null),
        apiService.get('/admin/drivers/online/count', { userType: 'admin' }).catch(() => null),
        apiService.get('/admin/suppliers/open/count', { userType: 'admin' }).catch(() => null),
      ]);
      if (ridesRes?.success) setActiveRidesCount(ridesRes.count ?? ridesRes.data ?? 0);
      if (onlineRes?.success) setOnlineDriversCount(onlineRes.count ?? onlineRes.data ?? 0);
      if (openRes?.success) setOpenBusinessesCount(openRes.count ?? 0);
    } catch {}
  }, []);

  // Stats livraison scopées à la période (pending/active restent « courant »).
  const fetchDelivery = useCallback(async () => {
    try {
      const res = await apiService.get(`/admin/delivery/stats?period=${period}`, { userType: 'admin' }).catch(() => null);
      if (res?.success && res.data) setDelivery(res.data);
    } catch {}
  }, [period]);

  // Résumé abonnements (revenus + nouveaux abonnements) sur la période.
  const fetchOverviewRevenue = useCallback(async () => {
    try {
      const { startDate, endDate } = rangeFor(period);
      const qs = startDate ? `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}` : '';
      const res = await apiService.get(`/admin/revenue/summary${qs}`, { userType: 'admin' }).catch(() => null);
      if (res?.success && res.summary) setOverview(res.summary);
    } catch {}
  }, [period]);

  useEffect(() => {
    fetchRealtimeKPIs();
    const interval = setInterval(fetchRealtimeKPIs, 30000);
    return () => clearInterval(interval);
  }, [fetchRealtimeKPIs]);

  // Recharger dès que la période change.
  useEffect(() => {
    fetchDelivery();
    fetchOverviewRevenue();
  }, [fetchDelivery, fetchOverviewRevenue]);

  if (loadingDrivers || loadingSubscriptions) {
    return (
      <div className="dashboard-content">
        <h1 className="page-title">Dashboard</h1>
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement des données...</p></div>
      </div>
    );
  }

  const go = (page) => () => setCurrentPage(page);

  const pendingDrivers = stats.pendingDrivers || 0;
  const pendingOrders = delivery?.pending_orders ?? 0;
  const todayOrders = delivery?.daily_orders?.find((d) => d.date === todayStr())?.count ?? 0;

  // ── A. À traiter : files d'attente actionnables (seulement si > 0) ──
  const actionItems = [
    { key: 'pd', count: pendingDrivers, label: 'Chauffeurs à valider', icon: <Car size={24} />, page: 'validation', tint: '#FFB900' },
    { key: 'po', count: pendingOrders, label: 'Commandes en attente', icon: <ShoppingBag size={24} />, page: 'delivery-orders', tint: '#DC2626' },
  ].filter((a) => a.count > 0);

  return (
    <div className="dashboard-content">
      <h1 className="page-title">Dashboard</h1>

      {/* A. À traiter */}
      <div className="dash-section">
        <h2 className="dash-section-title">À traiter</h2>
        {actionItems.length === 0 ? (
          <div className="dash-allclear"><CheckCircle size={20} /> Rien en attente — tout est à jour.</div>
        ) : (
          <div className="stats-grid">
            {actionItems.map((a) => (
              <div key={a.key} className="stat-card clickable" onClick={go(a.page)} style={{ borderLeftColor: a.tint, cursor: 'pointer' }}>
                <div className="stat-icon" style={{ background: a.tint }}>{a.icon}</div>
                <div className="stat-info">
                  <p className="stat-label">{a.label}</p>
                  <h2 className="stat-value">{fmt(a.count)}</h2>
                </div>
                <ArrowRight size={20} style={{ color: '#9AA0A8', alignSelf: 'center' }} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* B. En direct */}
      <div className="dash-section">
        <h2 className="dash-section-title">En direct <span className="dash-live-dot" /></h2>
        <div className="stats-grid">
          <StatCard icon={<Navigation size={24} />} label="Courses actives" value={fmt(activeRidesCount)} tint="#f97316" onClick={go('active-rides')} />
          <StatCard icon={<Truck size={24} />} label="Chauffeurs en ligne" value={fmt(onlineDriversCount)} tint="#10B981" onClick={go('online-drivers')} />
          <StatCard icon={<Store size={24} />} label="Business ouverts" value={fmt(openBusinessesCount)} tint="#059669" onClick={go('open-businesses')} />
          <StatCard icon={<Package size={24} />} label="Livraisons en cours" value={fmt(delivery?.active_orders)} tint="#F54119" onClick={go('delivery-orders')} />
          <StatCard icon={<ShoppingBag size={24} />} label="Commandes aujourd'hui" value={fmt(todayOrders)} tint="#FF7800" onClick={go('delivery-orders')} />
        </div>
      </div>

      {/* C. Vue d'ensemble (filtrable par période) */}
      <div className="dash-section">
        <div className="dash-section-head">
          <h2 className="dash-section-title">Vue d'ensemble</h2>
          <div className="dash-period-filter">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                className={`dash-period-btn${period === p.key ? ' active' : ''}`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="stats-grid">
          <StatCard icon={<DollarSign size={24} />} label="Revenus abonnements" value={`${fmt(overview?.totalRevenue)} FC`} tint="#10B981" onClick={go('revenue')} />
          <StatCard icon={<DollarSign size={24} />} label="Revenus livraison" value={`${fmt(delivery?.period_revenue)} FC`} tint="#10b981" onClick={go('delivery-orders')} />
          <StatCard icon={<Users size={24} />} label="Nouveaux abonnements" value={fmt(overview?.totalSubscriptions)} tint="#FF7800" onClick={go('abonnes')} />
          <StatCard icon={<ShoppingBag size={24} />} label="Commandes" value={fmt(delivery?.period_orders)} tint="#FF7800" onClick={go('delivery-orders')} />
          <StatCard icon={<ShoppingBag size={24} />} label="Commandes livrées" value={fmt(delivery?.period_delivered)} tint="#14b8a6" onClick={go('delivery-orders')} />
          <StatCard icon={<Store size={24} />} label="Nouveaux fournisseurs" value={fmt(delivery?.new_suppliers)} tint="#FFB900" onClick={go('delivery-suppliers')} />
        </div>
      </div>
    </div>
  );
}
